import React, { useEffect, useRef, useState } from 'react';
import { MoveHorizontal } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * The ONE chart renderer.
 *
 * 🚨 Extracted from `pages/Reports.tsx` (2026-07-24), where it lived inline.
 * It is now imported by BOTH surfaces — the Reports page and the Orchestrator
 * chat, which receives the same payload over the SSE `visualization` frame.
 * There must never be a second implementation: the backend emits one shape
 * (`utils/report_visualizations.py`), and two renderers drifting apart would
 * mean the same numbers look different depending on which screen you opened.
 *
 * The contract below is the backend's, field for field. Nothing here invents,
 * derives or reformats a value — a chart that is more confident than its data
 * is exactly the failure the tool-emitted-only rule exists to prevent.
 */

export interface YKey {
  key: string;
  label: string;
  color: string;
}

export interface Visualization {
  type: 'line_chart' | 'bar_chart' | 'pie_chart' | 'table';
  title: string;
  x_key?: string;
  y_keys?: YKey[];
  data?: Record<string, unknown>[];
  format?: string;
  unit?: string;
  columns?: string[];
  rows?: string[][];
}

/**
 * Width of the chart's own box, not the viewport.
 *
 * 🚨 A media query is the wrong instrument here. The same chart renders in a
 * two-up grid on the Reports page and inside a chat bubble that is itself
 * indented by an avatar — at 1280px of viewport the Orchestrator's chart box
 * can be narrower than the Reports page's is at 768px. Measuring the element
 * is the only way the "is this cramped?" question gets a true answer on both
 * surfaces.
 */
function useBoxWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    // Older Safari / jsdom. Staying at 0 means "assume roomy", which is the
    // pre-existing behaviour — never a crash and never a blank chart.
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0;
      setWidth((prev) => (Math.abs(prev - next) < 1 ? prev : next));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

/** Below this the axis, legend and pie labels all have to give ground. */
const NARROW_PX = 420;

/**
 * 🚨 THE X AXIS IS GRAIN-AGNOSTIC AND MUST STAY THAT WAY.
 *
 * `x_key` values are category labels rendered VERBATIM — there is deliberately
 * no `tickFormatter` on the X axis and no date parsing anywhere in this file.
 * The backend owns the bucket label, so `2026-W29`, `July 2026`, `Q3 2026` and
 * `2026-07-15` are all just strings here. Never add client-side reformatting:
 * the moment this file tries to parse a period key it bakes in a grain
 * assumption, and it will be a different one than the backend's.
 *
 * What DOES change with grain is the label LENGTH and the bucket COUNT, and
 * both are handled by measurement below — never by a constant.
 */

/** Longest x label in the series. Decides how much breathing room ticks need. */
function maxLabelLength(data: Record<string, unknown>[], xKey: string): number {
  let max = 0;
  for (const row of data) {
    const len = String(row?.[xKey] ?? '').length;
    if (len > max) max = len;
  }
  return max;
}

/**
 * Tick spacing, in px of clear space BETWEEN labels.
 *
 * recharts measures each label's rendered width and drops ticks that would
 * collide, so this is only the padding on top of that — which is why long
 * month/quarter labels thin themselves out instead of overlapping, and why 90
 * daily buckets render a handful of readable ticks rather than a grey smear.
 * `interval="preserveStartEnd"` then guarantees the first and last survive the
 * thinning (their fit is a separate problem — see `makeEdgeSafeTick`).
 *
 * Long labels get a wider gap because the collision test is exact: two labels
 * that miss each other by 5px are technically not overlapping and still read as
 * one word.
 */
function xTickGap(narrow: boolean, longestLabel: number): number {
  const long = longestLabel > 8;
  if (narrow) return long ? 24 : 16;
  return long ? 14 : 5;
}

/** Rough width of one tick character at `fontSize: 10`. Estimated, deliberately. */
const TICK_CHAR_PX = 5.6;

/**
 * An x tick that cannot fall off either edge of the surface.
 *
 * 🚨 recharts DOES try to keep the first and last labels inside the plot — it
 * shifts them by a MEASURED text width. That measurement is taken at the axis
 * group's inherited font size, not the 10px the tick actually renders at, and
 * it is not even consistently wrong: dumping the DOM at a 375px box, a
 * `2026-07-29` label measured 30px against ~52px rendered and its last glyph
 * was sliced off by the surface edge, while `December 2027` on the chart above
 * it measured almost exactly right. Twelve `2026-W29` buckets never exposed
 * this — short labels have a small absolute error. Month, quarter and ISO-date
 * labels do not, which is why the grain change is what surfaced it.
 *
 * Rather than pad the plot to absorb an error of unknown size (which costs
 * chart width on the narrowest screens, where it is most expensive), the final
 * position is decided HERE, at the size the text really renders at. Estimated,
 * not measured — but the estimate only has to be good enough to keep a label
 * inside a boundary it is a few px from crossing, and it degrades to recharts'
 * own placement when the box has not been measured yet.
 *
 * Middle anchoring is preserved: each label moves only as far as it must, so
 * recharts' own overlap thinning (which assumed a centred label) stays valid.
 *
 * `slotPx` — set only on a CATEGORY axis (see `categorical` below), where every
 * tick is drawn and a label wider than its slot would run into its neighbour.
 * There it is clipped to the slot with an ellipsis; the full value is still in
 * the tooltip. Nothing is clipped when the label fits, and on a time axis
 * `slotPx` is absent and no label is ever altered.
 */
function makeEdgeSafeTick(surfaceWidth: number, slotPx = 0) {
  return function EdgeSafeTick(props: {
    x?: number;
    y?: number;
    payload?: { value?: unknown };
  }) {
    const { x = 0, y = 0, payload } = props;
    const full = String(payload?.value ?? '');
    const fits = slotPx > 0 ? Math.floor(slotPx / TICK_CHAR_PX) : full.length;
    const label = full.length > fits && fits > 1 ? `${full.slice(0, fits - 1)}…` : full;
    const half = (label.length * TICK_CHAR_PX) / 2;
    // `width === 0` means the box was never measured (no ResizeObserver) —
    // leave recharts' placement alone rather than clamping against a lie.
    const cx =
      surfaceWidth > 0 ? Math.min(Math.max(x, half + 2), surfaceWidth - half - 4) : x;
    return (
      // 🚨 The class is LOAD-BEARING, not cosmetic. `CartesianAxis` measures
      // the first `.recharts-cartesian-axis-tick-value` in the DOM to learn what
      // font size to do its overlap maths at. A custom tick without the class
      // leaves that measurement empty, recharts falls back to a larger default,
      // over-estimates every label and thins a readable four-quarter axis down
      // to two ticks. Caught by counting ticks before/after in the screenshots.
      <text
        className="recharts-cartesian-axis-tick-value"
        x={cx}
        y={y}
        dy={10}
        textAnchor="middle"
        fill="currentColor"
        fontSize={10}
      >
        {label}
      </text>
    );
  };
}

/**
 * Shown INSTEAD of a chart when a well-formed spec carries no plottable data.
 *
 * 🚨 Not the same thing as the unknown-type note at the bottom of the file.
 * This spec is fine — the workspace simply has nothing in these buckets, which
 * is the normal answer for a month-grain question over a young workspace. An
 * empty pair of axes reads as "the chart is broken"; this says what is true.
 */
const EmptyNote: React.FC = () => (
  <p className="py-8 text-center text-xs text-gray-500 dark:text-gray-400">
    No data for this period.
  </p>
);

/**
 * Spread onto every series to turn the entry animation off, for two reasons —
 * and the first one is a correctness bug, not a preference.
 *
 * 1. Recharts renders pie labels ONLY after the entry animation reports
 *    finished. In the chat transcript the turn re-renders on every token
 *    flush (~60ms), so the animation restarts before it can finish and the
 *    percentages never appear at all. Found by dumping the SVG at 375px: the
 *    sectors were there and `recharts-pie-label-text` was absent entirely.
 * 2. A chart that replays its grow-in animation sixteen times while the
 *    answer around it is still being written is noise, not delight.
 */
const NO_ANIM = { isAnimationActive: false } as const;

/**
 * 🚨 Legend height is NOT free, and `ResponsiveContainer` will not find it for
 * you — it only ever solves for width. The legend is laid out below the plot
 * and steals height from it, so a fixed 200px box with six series leaves the
 * chart itself about 140px tall, which is where a "responsive" chart stops
 * being readable.
 */
function chartHeight(seriesCount: number, narrow: boolean): number {
  // Narrow charts have NO recharts legend at all (see `ChartLegend`), so the
  // whole box is plot and nothing needs budgeting for.
  if (narrow) return 180;
  const extraRows = Math.max(0, Math.ceil(seriesCount / 3) - 1);
  return 200 + extraRows * 20;
}

const LEGEND_STYLE = { fontSize: 11, paddingTop: 2 } as const;

/**
 * The narrow-width legend, in HTML, outside the SVG.
 *
 * 🚨 Both recharts legend layouts were tried at 375px and BOTH failed — this
 * is the third attempt and the reason it is hand-rolled:
 *   - `layout="horizontal"` (the default) does not reflow its items into the
 *     container, so with six series the last item on each row was cut off at
 *     the card's edge.
 *   - `layout="vertical" align="left"` stopped clipping but drew the legend
 *     OVER the axis, because recharts reserves legend space from the chart box
 *     and the two layouts disagree about which box that is.
 *
 * A flex-wrap list below the chart has neither failure mode: it is normal DOM
 * flow, so it wraps at any width and cannot overlap anything. Wide charts keep
 * recharts' own legend, which is fine with room to work in.
 */
const ChartLegend: React.FC<{ items: { label: string; color: string }[] }> = ({ items }) => (
  <ul className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
    {items.map((item) => (
      <li
        key={item.label}
        className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300 min-w-0"
      >
        <span
          className="w-2.5 h-2.5 rounded-sm shrink-0"
          style={{ backgroundColor: item.color }}
          aria-hidden="true"
        />
        <span className="min-w-0 break-words">{item.label}</span>
      </li>
    ))}
  </ul>
);

interface VisualizationRendererProps {
  viz: Visualization;
  /**
   * `false` drops the `.card` chrome — for the chat transcript, where the
   * chart already sits inside a message bubble and a card inside a card reads
   * as a rendering bug.
   */
  framed?: boolean;
}

const VisualizationRenderer: React.FC<VisualizationRendererProps> = ({ viz, framed = true }) => {
  const { ref, width } = useBoxWidth<HTMLDivElement>();
  const narrow = width > 0 && width < NARROW_PX;
  const { theme } = useTheme();
  const dark = theme === 'dark';

  /**
   * 🚨 recharts' default tooltip is a HARDCODED white box, and its label — the
   * one carrying the period ("July 2026"), the single most important string in
   * the popup — has no colour of its own, so it inherits the app's text colour.
   * In dark mode that is near-white ON white: invisible. Styled explicitly in
   * both directions rather than patched for dark only.
   *
   * Item colours are deliberately NOT overridden: in recharts the item text
   * colour IS the series colour, and it is the only thing mapping a row of the
   * tooltip back to its line. The dark surface is gray-900 rather than the
   * card's gray-800 so the mid-tone palette clears it better.
   */
  const tooltipStyles = {
    contentStyle: {
      backgroundColor: dark ? '#111827' : '#ffffff',
      border: `1px solid ${dark ? '#374151' : '#e5e7eb'}`,
      borderRadius: 8,
      fontSize: 12,
      boxShadow: dark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.08)',
    },
    labelStyle: { color: dark ? '#f9fafb' : '#111827', fontWeight: 600 },
  } as const;

  /**
   * The hover cursor is a `#ccc` block by default: a bright grey slab over a
   * dark card, and heavy enough in light mode to hide the bar it is meant to
   * point at. A tint of the surface reads as a highlight in both themes.
   */
  const barCursor = { fill: dark ? 'rgba(255,255,255,0.07)' : 'rgba(17,24,39,0.05)' } as const;
  const lineCursor = { stroke: dark ? '#6b7280' : '#9ca3af', strokeWidth: 1 } as const;

  /**
   * Axis ink. recharts defaults ticks to `#666`, which on a dark card is about
   * 2.6:1 — under AA, and it is the axis that carries the period labels.
   * `currentColor` + a Tailwind colour on the container keeps one source of
   * truth for both modes and matches the grid, which already inherits it.
   */
  const axisTick = { fontSize: 10, fill: 'currentColor' } as const;
  const axisLine = { stroke: 'currentColor', opacity: 0.25 } as const;
  const chartInk = 'text-gray-600 dark:text-gray-300';

  // Two formatters, not one: recharts types `tickFormatter` as returning
  // `string` and `Tooltip`'s `formatter` as returning a ReactNode. Sharing one
  // callback is what the inline version got away with only because its
  // parameter was implicitly `any`.
  const axisFmt = (v: unknown): string => (viz.unit ? `${v}${viz.unit}` : String(v));
  const tooltipFmt = (v: number) => (viz.unit ? `${v}${viz.unit}` : v);
  const seriesCount = viz.y_keys?.length ?? 0;

  /**
   * Bucket count and label length are now BOTH variable — the same question
   * answers as 12 weekly buckets (`2026-W29`), 24 monthly ones (`July 2026`) or
   * 90 daily ones, depending on the grain the backend picked. Everything that
   * depends on either is derived per payload here; nothing downstream assumes a
   * count or a label width.
   */
  const bucketCount = viz.data?.length ?? 0;
  const xLabelLen = viz.data && viz.x_key ? maxLabelLength(viz.data, viz.x_key) : 0;
  const tickGap = xTickGap(narrow, xLabelLen);
  const chartMargin = { top: 4, right: 4, left: 0, bottom: 0 } as const;

  /**
   * 🚨 A BAR CHART'S X AXIS IS CATEGORIES, NOT TIME, and the two want opposite
   * things from tick thinning.
   *
   * Dropping every other label on a time axis is correct — the line between the
   * labelled points is continuous, so an unlabelled position is still readable
   * by interpolation. Doing the same to bars is not: each bar is a separate
   * thing, and a bar with no label underneath it cannot be identified at all.
   * Rendered at 375px, a six-channel `CSAT by channel` chart silently dropped
   * `voice` and `instagram` — two bars the reader simply could not name. Every
   * bar chart in the app is categorical (escalation reasons, CSAT distribution,
   * ticket status, and now compiled queries grouped by a dimension), so this
   * was live before the chart builder; the builder just makes it common.
   *
   * `interval={0}` therefore draws EVERY category, and the tick clips to its
   * slot instead of colliding. The cap keeps a bar chart that is really a time
   * series (90 daily buckets) on the thinning path, where it belongs.
   */
  const categorical = viz.type === 'bar_chart' && bucketCount > 0 && bucketCount <= 8;
  // The plot, not the surface: the Y axis gutter is not available to ticks.
  const slotPx = categorical && width > 0 ? Math.max(0, width - 46) / bucketCount : 0;
  const xTick = makeEdgeSafeTick(width, slotPx);

  /**
   * 🚨 A ONE-BUCKET LINE DRAWS ABSOLUTELY NOTHING with `dot={false}`: a
   * polyline through a single point has no length, so the chart renders axes
   * and empty space and looks broken. That is not a hypothetical — a
   * month-grain question over a young workspace returns exactly one bucket.
   * Dots come back on for a series too short to draw a visible line.
   */
  const lineDots = bucketCount <= 2 ? ({ r: 3 } as const) : false;

  /**
   * A pie of zeroes is not a pie. recharts draws no sectors when every value is
   * 0, leaving a titled empty box — the empty note is the honest answer.
   */
  const pieTotal =
    viz.type === 'pie_chart'
      ? (viz.data ?? []).reduce((sum, e) => sum + (Number((e as { value?: unknown }).value) || 0), 0)
      : 0;

  /**
   * Does the table actually overflow its box? Measured, never guessed — the
   * "scroll sideways" hint has to be true, and a three-column table inside a
   * wide card does not overflow at all. Re-checked whenever the outer box
   * resizes, which is the only thing that can change the answer (the rows
   * themselves never change for a given payload).
   */
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [tableOverflows, setTableOverflows] = useState(false);
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    setTableOverflows(el.scrollWidth > el.clientWidth + 1);
  }, [width, viz]);

  /**
   * 🚨 Nothing below is trusted to exist. This payload arrives over SSE from a
   * backend that is free to add a `type` this build has never heard of, and a
   * chart that throws takes the whole transcript down with it — including the
   * answer the user actually asked for. Every branch is guarded, and an
   * unrecognized or incomplete one falls through to a quiet note.
   */
  const known =
    (viz.type === 'line_chart' && !!viz.data && !!viz.x_key && !!viz.y_keys) ||
    (viz.type === 'bar_chart' && !!viz.data && !!viz.x_key && !!viz.y_keys) ||
    (viz.type === 'pie_chart' && !!viz.data) ||
    (viz.type === 'table' && !!viz.columns && !!viz.rows);

  return (
    <div
      ref={ref}
      className={
        framed
          ? 'card p-4 space-y-3'
          : 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-3 sm:p-4 space-y-3'
      }
    >
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 break-words">
        {viz.title}
      </h3>

      {viz.type === 'line_chart' && viz.data && viz.x_key && viz.y_keys && (
        bucketCount === 0 ? <EmptyNote /> : (
        <>
        <ResponsiveContainer
          width="100%"
          height={chartHeight(seriesCount, narrow)}
          className={chartInk}
        >
          <LineChart data={viz.data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="opacity-10" />
            {/* `preserveStartEnd` + a measured gap is what stops a dozen period
                labels from becoming a grey smear at 375px — and it is grain
                blind, so "July 2026" simply earns a wider gap than "2026-W29"
                and recharts thins the ticks accordingly. */}
            <XAxis
              dataKey={viz.x_key}
              tick={xTick}
              axisLine={axisLine}
              tickLine={axisLine}
              interval="preserveStartEnd"
              minTickGap={tickGap}
            />
            <YAxis
              tick={axisTick}
              axisLine={axisLine}
              tickLine={axisLine}
              width={narrow ? 34 : 46}
              tickFormatter={axisFmt}
            />
            <Tooltip formatter={tooltipFmt} cursor={lineCursor} {...tooltipStyles} />
            {!narrow && <Legend wrapperStyle={LEGEND_STYLE} />}
            {viz.y_keys.map((yk) => (
              <Line key={yk.key} type="monotone" dataKey={yk.key} name={yk.label}
                stroke={yk.color} strokeWidth={2} dot={lineDots} {...NO_ANIM} />
            ))}
          </LineChart>
        </ResponsiveContainer>
        {narrow && <ChartLegend items={viz.y_keys} />}
        </>
        )
      )}

      {viz.type === 'bar_chart' && viz.data && viz.x_key && viz.y_keys && (
        bucketCount === 0 ? <EmptyNote /> : (
        <>
        <ResponsiveContainer
          width="100%"
          height={chartHeight(seriesCount, narrow)}
          className={chartInk}
        >
          <BarChart data={viz.data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="opacity-10" />
            <XAxis
              dataKey={viz.x_key}
              tick={xTick}
              axisLine={axisLine}
              tickLine={axisLine}
              interval={categorical ? 0 : 'preserveStartEnd'}
              minTickGap={tickGap}
            />
            <YAxis
              tick={axisTick}
              axisLine={axisLine}
              tickLine={axisLine}
              width={narrow ? 34 : 46}
              tickFormatter={axisFmt}
            />
            <Tooltip formatter={tooltipFmt} cursor={barCursor} {...tooltipStyles} />
            {!narrow && <Legend wrapperStyle={LEGEND_STYLE} />}
            {viz.y_keys.map((yk) => (
              <Bar key={yk.key} dataKey={yk.key} name={yk.label} fill={yk.color} radius={[3, 3, 0, 0]}
                {...NO_ANIM} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        {narrow && <ChartLegend items={viz.y_keys} />}
        </>
        )
      )}

      {viz.type === 'pie_chart' && viz.data && (
        bucketCount === 0 || pieTotal <= 0 ? <EmptyNote /> : (
        <>
        {/* Taller than the others on purpose: pie labels are drawn OUTSIDE
            the radius, so the 180px box the axis charts use clipped every
            percentage off the top and bottom slices at 375px. */}
        <ResponsiveContainer width="100%" height={narrow ? 210 : 200}>
          <PieChart>
            {/* Narrow: the slice carries the percentage only and the names move
                into a legend below. Spelling "Escalated to a human 34%" onto a
                slice inside a 340px box truncates it to "Escalated to…", which
                is worse than no label at all. */}
            <Pie
              data={viz.data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={narrow ? 55 : 75}
              label={
                narrow
                  ? ({ percent }: { percent: number }) => `${(percent * 100).toFixed(0)}%`
                  : ({ name, percent }: { name: string; percent: number }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
              }
              labelLine={!narrow}
              {...NO_ANIM}
            >
              {viz.data.map((entry, i) => (
                <Cell key={i} fill={(entry as { color?: string }).color ?? '#6366f1'} />
              ))}
            </Pie>
            <Tooltip {...tooltipStyles} />
          </PieChart>
        </ResponsiveContainer>
        {narrow && (
          <ChartLegend
            items={viz.data.map((e) => ({
              label: String((e as { name?: unknown }).name ?? ''),
              color: (e as { color?: string }).color ?? '#6366f1',
            }))}
          />
        )}
        </>
        )
      )}

      {viz.type === 'table' && viz.columns && viz.rows && (
        viz.rows.length === 0 ? <EmptyNote /> : (
        // 🚨 The table scrolls inside its own box. Without this the widest row
        // sets the width of the whole chat column and the page itself scrolls
        // sideways at 375px.
        //
        // 🚨 And it says so. Rendering this at 375px showed a five-column
        // conversation-sample table displaying two columns with NO indication
        // the other three existed: mobile scrollbars are invisible until you
        // are already scrolling, so a silent `overflow-x-auto` hides data from
        // the exact user who most needs to know it is there. The fade and the
        // hint appear only when the content actually overflows, measured — so
        // a table that fits gets neither.
        <div className="relative">
        <div ref={tableScrollRef} className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                {viz.columns.map((col) => (
                  <th key={col} className="text-left py-2 px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viz.rows.map((row, i) => (
                <tr key={i} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  {row.map((cell, j) => (
                    <td key={j} className="py-2 px-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          {tableOverflows && (
            <>
              <div
                className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white dark:from-gray-800 to-transparent rounded-r-xl"
                aria-hidden="true"
              />
              <p className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                <MoveHorizontal className="w-3 h-3 shrink-0" />
                Scroll sideways for all {viz.columns.length} columns
              </p>
            </>
          )}
        </div>
        )
      )}

      {!known && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          This chart can&apos;t be displayed in this version of the app.
        </p>
      )}
    </div>
  );
};

export default VisualizationRenderer;
